import React, { useState, useRef } from 'react';
import '../styles/home.css';
import resizeJpgIcon from '../assets/resize-jpg-icon.svg';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';

const ResizeJpg = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [quality, setQuality] = useState(80);
  const [outputFilename, setOutputFilename] = useState('');
  const [outputFormat, setOutputFormat] = useState('jpg');
  const [submitValue, setSubmitValue] = useState('Resize');
  const [resizedResult, setResizedResult] = useState(null);
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const aspectRatio = useRef(1);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setOriginalDimensions({ width: 0, height: 0 });
    setWidth('');
    setHeight('');
    setMaintainAspectRatio(true);
    setQuality(80);
    setOutputFilename('');
    setOutputFormat('jpg');
    setSubmitValue('Resize');
    setResizedResult(null);
    setNotify('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.type.match(/image\/(jpeg|jpg)/i)) {
      setNotify('Only JPG/JPEG images are allowed');
      setError('error');
      return;
    }

    setFile(selectedFile);
    setNotify('');
    setError(null);
    setResizedResult(null);
    setOutputFilename(selectedFile.name.replace(/\.[^/.]+$/, ''));

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalDimensions({ width: img.width, height: img.height });
        setWidth(img.width.toString());
        setHeight(img.height.toString());
        aspectRatio.current = img.width / img.height;
      };
      img.src = e.target.result;
      setPreview(e.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleWidthChange = (e) => {
    const newWidth = e.target.value;
    setWidth(newWidth);
    if (maintainAspectRatio && newWidth) {
      setHeight(Math.round(parseInt(newWidth) / aspectRatio.current).toString());
    }
  };

  const handleHeightChange = (e) => {
    const newHeight = e.target.value;
    setHeight(newHeight);
    if (maintainAspectRatio && newHeight) {
      setWidth(Math.round(parseInt(newHeight) * aspectRatio.current).toString());
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const resizeImage = async () => {
    if (!file || !width || !height) return;

    setSubmitValue(<Spinner />);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = parseInt(width);
        canvas.height = parseInt(height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const mimeType = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            const filename = `${outputFilename || 'resized'}.${outputFormat}`;
            setResizedResult({
              blob,
              url: URL.createObjectURL(blob),
              filename,
              originalSize: formatFileSize(file.size),
              newSize: formatFileSize(blob.size),
              dimensions: `${width} × ${height}`,
            });
            setSubmitValue('Success');
            addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
          },
          mimeType,
          quality / 100,
        );
      };
      img.src = preview;
    } catch (err) {
      console.error('Resize error:', err);
      setNotify('Failed to resize image. Please try again.');
      setError('error');
      setSubmitValue('Resize');
    }
  };

  const handleDownload = () => {
    if (!resizedResult) return;
    const a = document.createElement('a');
    a.href = resizedResult.url;
    a.download = resizedResult.filename;
    a.click();
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <img src={resizeJpgIcon} alt="Resize JPG" loading="lazy" />
        </div>
        <h3 className="service-card-title">Resize JPG</h3>
        <p className="service-card-desc">Change dimensions and resize JPG images online</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {!resizedResult ? (
                <Button variant="primary" disabled={!file || !width || !height || error} onClick={resizeImage}>
                  {submitValue}
                </Button>
              ) : (
                <Button variant="primary" onClick={handleDownload}>
                  Download
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Resize JPG Image"
      >
        <div>
          {/* File Upload */}
          <div className="bordered-box">
            <h6 className="container-header">📷 Choose JPG Image</h6>
            <FormField label="Drag & drop your JPG/JPEG here, or click to browse" errorText={notify}>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,image/jpeg" onChange={handleFileChange} />
            </FormField>
            {preview && (
              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                />
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                  Original: {originalDimensions.width} × {originalDimensions.height} | {formatFileSize(file?.size || 0)}
                </div>
              </div>
            )}
          </div>

          {/* Resize Options */}
          {file && !resizedResult && (
            <div className="bordered-box">
              <h6 className="container-header">📏 Resize Options</h6>

              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                <FormField label="Width (px)">
                  <input
                    type="number"
                    value={width}
                    onChange={handleWidthChange}
                    style={{ width: '100px', padding: '8px', border: '1px solid #d1d1d1', borderRadius: '4px' }}
                  />
                </FormField>
                <span style={{ marginTop: '20px' }}>×</span>
                <FormField label="Height (px)">
                  <input
                    type="number"
                    value={height}
                    onChange={handleHeightChange}
                    style={{ width: '100px', padding: '8px', border: '1px solid #d1d1d1', borderRadius: '4px' }}
                  />
                </FormField>
                <label
                  style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={maintainAspectRatio}
                    onChange={(e) => setMaintainAspectRatio(e.target.checked)}
                  />
                  🔗 Lock ratio
                </label>
              </div>

              <FormField label="🎯 Quality">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: '40px' }}>{quality}%</span>
                </div>
              </FormField>

              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                <FormField label="📝 Output Filename">
                  <input
                    type="text"
                    value={outputFilename}
                    onChange={(e) => setOutputFilename(e.target.value)}
                    placeholder="resized"
                    style={{ width: '150px', padding: '8px', border: '1px solid #d1d1d1', borderRadius: '4px' }}
                  />
                </FormField>
                <FormField label="Format">
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    style={{ padding: '8px', border: '1px solid #d1d1d1', borderRadius: '4px' }}
                  >
                    <option value="jpg">JPG</option>
                    <option value="png">PNG</option>
                  </select>
                </FormField>
              </div>
            </div>
          )}

          {/* Results */}
          {resizedResult && (
            <div className="bordered-box">
              <h6 className="container-header">✅ Resize Complete!</h6>

              <div
                style={{
                  backgroundColor: '#e6f7e6',
                  border: '1px solid #52c41a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <span style={{ color: '#135200' }}>🚫 No watermark added • 🔒 100% Private • 🆓 Free</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Original</div>
                  <div style={{ fontWeight: '600' }}>
                    {originalDimensions.width} × {originalDimensions.height}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{resizedResult.originalSize}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e6f7e6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>Resized</div>
                  <div style={{ fontWeight: '600', color: '#52c41a' }}>{resizedResult.dimensions}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{resizedResult.newSize}</div>
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <img
                  src={resizedResult.url}
                  alt="Resized"
                  style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              </div>

              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <Button variant="link" onClick={resetState}>
                  Resize Another Image
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ResizeJpg;
